// Agentes: bots com instruções fixas, ao estilo dos Gems do Gemini (/api/client/agents*).
// Cada agente tem sua própria lista de conversas — ver Chat.tsx (prop agentId) e a rota /agentes/:id.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useNavigate } from 'react-router';
import { Camera, Check, ChevronRight, Pencil, Plus, Trash2, X } from 'lucide-react';
import { api, AGENT_COLORS, AGENT_ICONS, type Agent, type AgentColor, type AgentIcon, type Doc } from '../api';
import { toast } from '../components/toast';
import { AGENT_COLOR_LABEL, AGENT_ICON_CMP, AgentAvatar } from '../components/agentMeta';
import '../styles/agents.css';

// `photo`: o que a tela mostra agora; `photoChanged` diz se vai no PUT (sem ele a foto salva permanece); `documentIds` substitui o conjunto
type Form = { id: string | null; name: string; description: string; icon: AgentIcon; color: AgentColor; systemPrompt: string; photo: string | null; photoChanged: boolean; documentIds: string[] };
const blank = (): Form => ({ id: null, name: '', description: '', icon: 'bot', color: 'fil', systemPrompt: '', photo: null, photoChanged: false, documentIds: [] });
const MAX_AGENT_DOCS = 10;

/** Foto do agente: recorte central quadrado, 256×256, JPEG 0,85 (~20–40 KB), como data URL. */
async function squarePhoto(file: File): Promise<string | null> {
  if (!file.type.startsWith('image/')) return null;
  const bmp = await createImageBitmap(file);
  const side = Math.min(bmp.width, bmp.height);
  const c = document.createElement('canvas'); c.width = 256; c.height = 256;
  c.getContext('2d')?.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, 256, 256);
  return c.toDataURL('image/jpeg', 0.85);
}

export function Agents() {
  const nav = useNavigate();
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [docs, setDocs] = useState<Doc[] | null>(null);       // documentos do usuário para vincular (carregam ao abrir o formulário)
  const [docsError, setDocsError] = useState(false);
  const [docQuery, setDocQuery] = useState('');
  const photoRef = useRef<HTMLInputElement>(null);

  const loadDocs = () => { setDocsError(false); setDocs(null); api<Doc[]>('/api/client/documents').then((l) => setDocs(l.filter((d) => d.chunkCount > 0))).catch(() => { setDocs([]); setDocsError(true); }); };

  const load = () => api<Agent[]>('/api/client/agents').then(setAgents).catch(() => setAgents([]));
  useEffect(() => { void load(); }, []);

  const openCreate = () => { setForm(blank()); setDocQuery(''); loadDocs(); };
  const openEdit = (a: Agent) => { setConfirming(null); setDocQuery(''); loadDocs(); setForm({ id: a.id, name: a.name, description: a.description ?? '', icon: a.icon, color: a.color, systemPrompt: a.systemPrompt, photo: a.photo, photoChanged: false, documentIds: a.documents.map((d) => d.id) }); };
  const pickPhoto = async (f: File | undefined) => {
    if (!f || !form) return;
    try { const url = await squarePhoto(f); if (!url) { toast('Escolha uma imagem', 'err'); return; } setForm({ ...form, photo: url, photoChanged: true }); }
    catch { toast('não foi possível ler a imagem', 'err'); }
  };
  const toggleDoc = (id: string) => { if (!form) return; const on = form.documentIds.includes(id); if (!on && form.documentIds.length >= MAX_AGENT_DOCS) { toast(`Máximo de ${MAX_AGENT_DOCS} documentos por agente`, 'err'); return; } setForm({ ...form, documentIds: on ? form.documentIds.filter((x) => x !== id) : [...form.documentIds, id] }); };
  const closeForm = () => setForm(null);

  const save = async () => {
    if (!form) return;
    const name = form.name.trim(), systemPrompt = form.systemPrompt.trim();
    if (!name) { toast('Dê um nome ao agente', 'err'); return; }
    if (!systemPrompt) { toast('Escreva as instruções do agente', 'err'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ name, description: form.description.trim(), icon: form.icon, color: form.color, systemPrompt, documentIds: form.documentIds, ...(form.photoChanged ? { photo: form.photo } : {}) });
      if (form.id) await api<Agent>(`/api/client/agents/${form.id}`, { method: 'PUT', body });
      else await api<Agent>('/api/client/agents', { method: 'POST', body });
      toast(form.id ? 'Agente atualizado' : 'Agente criado');
      setForm(null);
      await load();
    } catch (e) { toast((e as Error).message, 'err'); }
    finally { setSaving(false); }
  };

  const del = async (id: string) => {
    setConfirming(null);
    try { await api(`/api/client/agents/${id}`, { method: 'DELETE' }); } catch (e) { toast((e as Error).message, 'err'); return; }
    setAgents((cur) => (cur ?? []).filter((a) => a.id !== id));
    toast('Agente apagado');
  };

  return (
    <>
      <div className="ph">
        <div>
          <span className="eyebrow">Ferramentas IA</span>
          <h1>Agentes</h1>
          <p>Crie bots com instruções fixas — cada um guarda o próprio jeito de responder e as próprias conversas.</p>
        </div>
        <div className="actions">
          {!form && <button className="btn primary" onClick={openCreate}><Plus />Novo agente</button>}
        </div>
      </div>

      {form && (
        <form className="card agent-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <div className="agent-form-h">
            <b>{form.id ? 'Editar agente' : 'Novo agente'}</b>
            <button type="button" className="btn sm ghost" onClick={closeForm}><X />fechar</button>
          </div>

          <div className="agent-form-grid">
            <label className="field"><span>Nome</span>
              <input autoFocus maxLength={60} placeholder="ex.: Revisor de contrato" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="field"><span>Descrição <span className="mute">· aparece no card, opcional</span></span>
              <input maxLength={200} placeholder="ex.: Revisa cláusulas e aponta riscos" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
          </div>

          <div className="field">
            <span>Ícone</span>
            <div className="agent-icons">
              {AGENT_ICONS.map((i) => { const Icon = AGENT_ICON_CMP[i]; return (
                <button key={i} type="button" className={`agent-icon-pick ${form.icon === i ? 'on' : ''}`} onClick={() => setForm({ ...form, icon: i })} aria-label={i} aria-pressed={form.icon === i}><Icon size={16} /></button>
              ); })}
            </div>
          </div>

          <div className="field">
            <span>Cor</span>
            <div className="agent-colors">
              {AGENT_COLORS.map((c) => (
                <button key={c} type="button" className={`agent-color-pick c-${c} ${form.color === c ? 'on' : ''}`} onClick={() => setForm({ ...form, color: c })} aria-label={AGENT_COLOR_LABEL[c]} aria-pressed={form.color === c} title={AGENT_COLOR_LABEL[c]} />
              ))}
            </div>
          </div>

          <div className="field">
            <span>Foto <span className="mute">· opcional, no lugar do ícone e da cor</span></span>
            <div className="agent-photo-row">
              <AgentAvatar icon={form.icon} color={form.color} size={22} photo={form.photo} name={form.name || 'Foto do agente'} />
              <input ref={photoRef} type="file" accept="image/*" hidden aria-label="Foto do agente" onChange={(e) => { void pickPhoto(e.target.files?.[0]); e.target.value = ''; }} />
              <button type="button" className="btn sm" onClick={() => photoRef.current?.click()}><Camera />{form.photo ? 'Trocar foto' : 'Escolher foto'}</button>
              {form.photo && <button type="button" className="btn sm ghost" onClick={() => setForm({ ...form, photo: null, photoChanged: true })}>Remover foto</button>}
            </div>
          </div>

          <div className="field">
            <span>Documentos <span className="mute">· o agente consulta em toda conversa · {form.documentIds.length}/{MAX_AGENT_DOCS}</span></span>
            {docs === null ? <div className="skel" style={{ height: 64 }} />
              : docsError ? <div className="agent-docs-empty">Não foi possível carregar. <button type="button" className="btn sm ghost" onClick={loadDocs}>Tentar de novo</button></div>
              : docs.length === 0 ? <div className="agent-docs-empty">Você ainda não tem documentos. <Link to="/documentos">Enviar em Documentos</Link></div>
              : (
                <div className="agent-docs">
                  {docs.length > 5 && <input className="agent-docs-q" type="search" placeholder="Buscar documento…" aria-label="Buscar documento" value={docQuery} onChange={(e) => setDocQuery(e.target.value)} />}
                  <div className="agent-docs-list" role="group" aria-label="Documentos do agente">
                    {docs.filter((d) => d.name.toLowerCase().includes(docQuery.trim().toLowerCase())).map((d) => (
                      <label key={d.id} className="agent-doc"><input type="checkbox" checked={form.documentIds.includes(d.id)} onChange={() => toggleDoc(d.id)} /><span>{d.name}</span></label>
                    ))}
                  </div>
                </div>
              )}
          </div>

          <label className="field"><span>Instruções <span className="mute">· como o agente deve se comportar</span></span>
            <textarea rows={5} maxLength={8000} placeholder="Você é um especialista em… responda sempre em…" value={form.systemPrompt} onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })} />
          </label>

          <div className="agent-form-foot">
            <span className="mute" style={{ fontSize: 12 }}>As instruções valem para todas as conversas deste agente.</span>
            <button className="btn primary" type="submit" disabled={saving}>{saving ? 'Salvando…' : 'Salvar agente'}</button>
          </div>
        </form>
      )}

      <section className="sec">
        <div className="sec-h"><h2>Seus agentes</h2>{agents && agents.length > 0 && <span className="n">{agents.length}</span>}</div>

        {agents === null ? (
          <div className="grid g3"><div className="skel" style={{ height: 128 }} /><div className="skel" style={{ height: 128 }} /><div className="skel" style={{ height: 128 }} /></div>
        ) : agents.length === 0 ? (
          <div className="empty"><b>Nenhum agente ainda</b><p>Crie um agente com instruções fixas — como um assistente jurídico, um revisor de código ou um redator — e comece a conversar com ele.</p></div>
        ) : (
          <div className="grid g3 agent-grid">
            {agents.map((a) => (
              <div key={a.id} className="agent-card" onClick={() => nav(`/agentes/${a.id}`)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') nav(`/agentes/${a.id}`); }}>
                <div className="agent-card-top">
                  <AgentAvatar icon={a.icon} color={a.color} size={20} photo={a.photo} name={a.name} />
                  <div className="agent-card-actions" onClick={(e) => e.stopPropagation()}>
                    {confirming === a.id ? (
                      <>
                        <button type="button" className="danger" onClick={() => void del(a.id)} aria-label="Confirmar: apagar agente"><Check /></button>
                        <button type="button" onClick={() => setConfirming(null)} aria-label="Cancelar"><X /></button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => openEdit(a)} aria-label={`Editar ${a.name}`} title="Editar"><Pencil /></button>
                        <button type="button" className="danger" onClick={() => setConfirming(a.id)} aria-label={`Apagar ${a.name}`} title="Apagar"><Trash2 /></button>
                      </>
                    )}
                  </div>
                </div>
                <b className="agent-card-name">{a.name}</b>
                <p className="agent-card-desc">{a.description || 'Sem descrição.'}</p>
                <div className="agent-card-foot">
                  <span className="mono">{a.chatCount} {a.chatCount === 1 ? 'conversa' : 'conversas'}{a.documents.length > 0 && ` · ${a.documents.length} ${a.documents.length === 1 ? 'documento' : 'documentos'}`}</span>
                  <ChevronRight />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
